import { toast } from "sonner";
import { Download, Share2, Link2, Facebook } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;
const slug = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "litter";

export function LitterShareDialog({ litter, onClose }) {
  const { t, lang } = useLanguage();
  if (!litter) return null;
  const img = `${API}/api/litters/${litter.id}/share-card.png?v=${encodeURIComponent(litter.status + (litter.title || "") + (litter.photo || ""))}`;
  const link = `${API}/api/lt/${slug(litter.title || `${litter.breed} ${litter.kind || ""}`)}-${litter.id.slice(0, 8)}${lang === "fr" ? "?lang=fr" : ""}`;
  const fb = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, "_blank", "noopener,noreferrer,width=640,height=560");
  const fileName = `critterposts-${litter.breed.toLowerCase().replace(/\s+/g, "-")}.png`;
  const getBlob = () => fetch(img).then((r) => r.blob());
  const download = async () => {
    const url = URL.createObjectURL(await getBlob());
    const a = document.createElement("a");
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  };
  const share = async () => {
    try {
      const file = new File([await getBlob()], fileName, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], text: link });
      else if (navigator.share) await navigator.share({ url: link });
      else download();
    } catch (e) { if (e?.name !== "AbortError") toast.error(e.message); }
  };
  const copy = async () => { await navigator.clipboard.writeText(link); toast.success(t("sh_copied")); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="litter-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">{t("sh_title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("sh_desc")}</DialogDescription>
        </DialogHeader>
        <img src={img} alt="" className="w-full aspect-square rounded-xl border border-slate-200 bg-slate-100" data-testid="litter-share-preview" />
        <div className="grid grid-cols-3 gap-2">
          <Button onClick={download} variant="outline" data-testid="litter-share-download" className="rounded-xl gap-1 text-xs"><Download size={14} /> {t("sh_download")}</Button>
          <Button onClick={share} data-testid="litter-share-native" className="rounded-xl gap-1 text-xs bg-[#F5551E] hover:bg-[#E04812] text-white"><Share2 size={14} /> {t("sh_share")}</Button>
          <Button onClick={copy} variant="outline" data-testid="litter-share-copy" className="rounded-xl gap-1 text-xs"><Link2 size={14} /> {t("sh_copy")}</Button>
        </div>
        <Button onClick={fb} data-testid="litter-share-facebook" className="w-full rounded-xl gap-1.5 bg-[#1877F2] hover:bg-[#166FE5] text-white">
          <Facebook size={15} /> {lang === "fr" ? "Partager sur Facebook" : "Share on Facebook"}
        </Button>
        <p className="text-[11px] text-slate-400 text-center -mt-1">{lang === "fr" ? "Le lien partagé affiche un aperçu avec photo et logo." : "Shared links show a branded photo preview."}</p>
      </DialogContent>
    </Dialog>
  );
}
